import { createContext, useContext, useState, useMemo } from 'react';
import WelcomeToWorkshopModal from '../../../components/modals/WelcomeToWorkshopModal';
import AddSkillsModal from '../../../components/modals/AddSkillsModal';
import CurriculumModal from '../../../components/modals/CurriculumModal';
import { getCurriculum } from '../../../utils/curriculum';
import { BookSelectorModal } from '../BookSelectorModal/BookSelectorModal';
import { ReviewSharedTemplateModal } from '../ReviewSharedTemplateModal';
import { useUnlimitedContext } from './UnlimitedContext';

const ModalsCtx = createContext(null);

/**
 * Returns the Unlimited modals context. Must be used within UnlimitedModalsProvider.
 */
export const useUnlimitedModals = () => {
  const ctx = useContext(ModalsCtx);
  if (!ctx) throw new Error('useUnlimitedModals must be used within an UnlimitedModalsProvider');
  return ctx;
};

/**
 * Owns all modal visibility state and renders the modals inside the provider,
 * following the HomepageModalsContext pattern. View components don't need to
 * wire up any modals — they just call setters like `setShowBookModal(true)`.
 */
export const UnlimitedModalsProvider = ({ children }) => {
  // Book selector
  const [showBookModal, setShowBookModal] = useState(false);

  // Standards
  const [showStandardsModal, setShowStandardsModal] = useState(false);
  const [standardsSubject, setStandardsSubject] = useState(null);

  // Curriculum (align-to-curriculum)
  const [showCurriculumModal, setShowCurriculumModal] = useState(false);
  const [curriculumId, setCurriculumId] = useState(null);

  // Welcome
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  // Shared template
  const [sharedTemplateIds, setSharedTemplateIds] = useState(null);

  const value = useMemo(
    () => ({
      // Book
      showBookModal,
      setShowBookModal,
      // Standards
      showStandardsModal,
      setShowStandardsModal,
      standardsSubject,
      setStandardsSubject,
      // Curriculum
      showCurriculumModal,
      setShowCurriculumModal,
      curriculumId,
      setCurriculumId,
      // Welcome
      showWelcomeModal,
      setShowWelcomeModal,
      // Shared template
      sharedTemplateIds,
      setSharedTemplateIds,
    }),
    [
      showBookModal,
      showStandardsModal,
      standardsSubject,
      showCurriculumModal,
      curriculumId,
      showWelcomeModal,
      sharedTemplateIds,
    ]
  );

  return <ModalsCtx.Provider value={value}>{children}</ModalsCtx.Provider>;
};

/**
 * Renders all Unlimited modals. Must be placed inside both UnlimitedModalsProvider
 * and UnlimitedProvider so it can read from both contexts.
 *
 * This follows the HomepageModalsContext pattern where modal rendering is
 * colocated with modal state, keeping view components clean.
 */
export const UnlimitedModals = () => {
  const modals = useUnlimitedModals();
  const { book, standards, curriculum, template } = useUnlimitedContext();

  return (
    <>
      <BookSelectorModal
        isOpen={modals.showBookModal}
        onClose={() => modals.setShowBookModal(false)}
        onConfirm={book.handleBookSelected}
      />

      {modals.showStandardsModal && (
        <AddSkillsModal
          showModal={modals.showStandardsModal}
          setShowModal={(show) => {
            modals.setShowStandardsModal(show);
            if (!show) modals.setStandardsSubject(null);
          }}
          onSubmit={standards.handleStandardsSelected}
          selectedLanguageStandards={standards.languageStandards}
          selectedContentStandard={standards.contentStandards}
          initialSubjectKey={modals.standardsSubject}
          maxSelectableOverride={5}
        />
      )}

      {modals.showCurriculumModal && getCurriculum(modals.curriculumId) && (
        <CurriculumModal
          showModal={modals.showCurriculumModal}
          setShowModal={(show) => {
            modals.setShowCurriculumModal(show);
            if (!show) modals.setCurriculumId(null);
          }}
          onSubmit={curriculum.handleCurriculumSelected}
          curriculumId={modals.curriculumId}
          // A selection from another curriculum names a course this one doesn't have.
          initialSelection={
            curriculum.selection?.curriculumId === modals.curriculumId ? curriculum.selection : null
          }
        />
      )}

      {modals.sharedTemplateIds?.length > 0 && (
        <ReviewSharedTemplateModal
          templateIds={modals.sharedTemplateIds}
          onClose={() => modals.setSharedTemplateIds(null)}
          onSave={(savedTemplate) => {
            template.setRefetchTemplatesTrigger((prev) => prev + 1);
            if (savedTemplate) {
              template.setSelectedTemplate(savedTemplate);
              modals.setSharedTemplateIds(null);
            }
          }}
          onUse={(ownedTemplate) => template.setSelectedTemplate(ownedTemplate)}
        />
      )}

      <WelcomeToWorkshopModal
        showModal={modals.showWelcomeModal}
        setShowModal={modals.setShowWelcomeModal}
      />
    </>
  );
};
